import { CartsDataViewContext } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { InvoicesDataViewContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/SavedOrderList/Handlers/UpdateSearchFields";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import translate from "@insite/client-framework/Translate";

const mapDispatchToProps = {
    updateSearchFields,
};

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps>;

export interface SavedOrderListPaginationStyles {
    pagination?: PaginationPresentationProps;
    wrapper?: InjectableCss;
    countText?: TypographyPresentationProps;
    itemsText?: TypographyPresentationProps;
    queryText?: TypographyPresentationProps;
    resultCount?: InjectableCss;
}

export const savedOrderListPaginationStyles: SavedOrderListPaginationStyles = {
    wrapper: {
        css: css`
            margin-top: .625rem;
            color: ${themeColors.colorCharcoalGrey};
            font-size: ${themeFontSizes.size14};
            letter-spacing: 0;
            display: flex;
            padding: 10px 0;
            margin-bottom: 0;
            margin-top: 5px;
            border-top: 1px solid #E4E8F0;
            border-bottom: 1px solid #E4E8F0;
            align-items: center;
            z-index: 1;
            position: relative;

            .order-paging {
                margin: 0;
                width: auto;
                justify-content: flex-start;
                padding-left: 25px;

                > span {
                    label {
                        color: #333F48;
                        font-family: 'Roboto', sans-serif;
                        font-size: 14px;
                        font-weight: 500;
                        letter-spacing: 0;
                        padding: 10px 15px 0 0;
                    }
                }

                select[data-test-selector="paginationPerPageSelect"] {
                    min-width: 79px;
                    border-color: #C2D1D9 !important;
                    opacity: .93;
                    background-color: #fff !important;
                    color: #161616 !important;
                    letter-spacing: 0.16px;
                }
            }

            nav {
                display: flex;
                justify-content: center;

                @media screen and (min-width: 1280px) {
                    width: 70%;
                    margin-top: 0;
                    ul.pagination-arrow li:nth-child(3) {
                        margin-left: 55px;
                    }

                    ul.pagination-arrow li:nth-last-child(2) {
                        margin-left: 55px;
                    }
                }

                @media screen and (min-width: 1199px) {
                    margin-top: 0;
                    margin-left: 60px;
                }

                @media screen and (min-width: 1400px) {
                    margin-left: 110px;
                }
            }

            @media screen and (max-width: 1279px) {
                margin-bottom: 15px;
                
                nav {
                    flex-basis: 100%;
                    max-width: 100%;
                }
                .order-paging {
                    width: 100%;
                    flex-basis: 100%;
                    padding-left: 0;
                    flex-wrap: wrap;
                    > span {
                        > div {
                            flex-direction: column;
                        }
                        label {
                            text-align: left;
                            line-height: 31px;
                            padding: 0 15px 0 0;

                            + div {
                                min-width: 149px;
                            }
                        }
                    }
                }
                
                .order-result-count {
                    font-weight: 600;
                    position: absolute;
                    right: 0;
                    width: auto;
                    top: 49px;
                }
            }

        `,
    },
    countText: {
        css: css`
            white-space: nowrap;
        `,
    },
    queryText: {
        css: css`
            white-space: nowrap;
            font-weight: bold;
        `,
    },
    resultCount: {
        css: css`
            display: flex;
            width: auto;
            color: #535E64;
            font-family: 'Lato',sans-serif;
            font-size: 14px;
            font-style: italic;
            letter-spacing: 0;
            line-height: 31px;

            span {
                color: #535E64;
                display: inline-block;
                padding-right: 5px;
            }

            @media screen and (min-width: 1280px) {
                width: auto;
            }
        `,
    },
};

const styles = savedOrderListPaginationStyles;

const SavedOrderListPagination = ({ updateSearchFields }: Props) => {
    const savedOrderListDataView = useContext(CartsDataViewContext);

    const changePage = (newPageIndex: number) => {
        updateSearchFields({
            page: newPageIndex,
        });
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        updateSearchFields({
            page: 1,
            pageSize: newPageSize,
        });
    };

    if (!savedOrderListDataView.value) {
        return null;
    }

    const { pagination } = savedOrderListDataView;
    if (!pagination || pagination.totalItemCount === 0) {
        return null;
    }

    const itemsText = (pagination.totalItemCount !== 1 ? " Results" : " Result");
    const pageSizeNew = (pagination.totalItemCount < pagination.pageSize ? pagination.totalItemCount : pagination.pageSize ); 

    return (
        <Typography as="div" {...styles.wrapper}>
            <StyledWrapper {...styles.resultCount} className="order-result-count"  data-test-selector="orderHistory_resultCount">
                <Typography {...styles.itemsText}>Showing </Typography>
                <Typography {...styles.countText} data-test-selector="orderHistory_CountText">
                    {pageSizeNew}
                </Typography>
                <Typography {...styles.itemsText}> of </Typography>
                <Typography {...styles.countText} data-test-selector="orderHistory_CountText">
                    {pagination.totalItemCount}
                </Typography>
                <Typography {...styles.itemsText}>{translate(itemsText)}</Typography>
            </StyledWrapper>

            <Pagination
                {...styles.pagination}
                resultsCount={pagination.totalItemCount}
                currentPage={pagination.page}
                resultsPerPage={pagination.pageSize}
                resultsPerPageOptions={pagination.pageSizeOptions}
                onChangePage={changePage}
                onChangeResultsPerPage={changeResultsPerPage}
                className="order-paging"
            />
        </Typography>

    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(SavedOrderListPagination),
    definition: {
        group: "Saved Order List",
        displayName: "Pagination",
        allowedContexts: ["SavedOrderListPage"],
    },
};

export default widgetModule;
